import { UUID } from '@app/_api/identifyable';
import {
  getNew,
  getTillNow,
  getType,
  SuggestionAction,
} from '@app/pages/admin-settings/subpages/admin-settings-suggestions/suggestion.types';

export type NotificationAction =
  | { type: 'CHECK_VISIBILITY' }
  | { type: 'SKILL_MERGED'; skillId: UUID; sourceTitle: string; targetTitle: string }
  | { type: 'SKILL_MOVED'; skillId: UUID; movedTitle: string; parentTitle: string }
  | { type: 'SUGGESTION_CREATED'; suggestionAction: SuggestionAction }
  | { type: 'SUGGESTION_ACCEPTED'; suggestionAction: SuggestionAction }
  | { type: 'SUGGESTION_REJECTED'; suggestionAction: SuggestionAction; reason?: string };

export interface NotificationDto {
  id: UUID;
  createdAt: Date;
  action: NotificationAction;
}

export function getNotificationTitle(notification: NotificationAction): string {
  switch (notification.type) {
    case 'CHECK_VISIBILITY':
      return 'Bitte aktualisiere Deine Sichtbarkeitseinstellungen';
    case 'SKILL_MERGED':
      return 'Skills wurden fusioniert';
    case 'SKILL_MOVED':
      return 'Skill wurde verschoben';
    case 'SUGGESTION_CREATED':
      return 'Vorschlag wurde erstellt';
    case 'SUGGESTION_ACCEPTED':
      return 'Vorschlag wurde akzeptiert';
    case 'SUGGESTION_REJECTED':
      return 'Vorschlag wurde abgelehnt';
    default:
      return notification satisfies never;
  }
}

export function getNotificationMessage(
  notificationAction: NotificationAction,
  getSkillName: (skillId: UUID) => string,
  getCertificateName: (certificateId: UUID) => string,
): string {
  switch (notificationAction.type) {
    case 'CHECK_VISIBILITY':
      return 'SkillTree lebt davon, dass sich iF Angestellte untereinander unterstützen. Du kannst entscheiden, mit wem Du Deine Skills teilen möchtest. Je mehr Angestellte ihre Skills teilen, desto besser können wir uns bei Fragen und Anliegen untereinander unterstützen.';
    case 'SKILL_MERGED':
      return `Der Skill "${notificationAction.sourceTitle}" wurde mit dem Skill "${notificationAction.targetTitle}" fusioniert. Bitte prüfe, ob die Zuweisung für Dich weiterhin korrekt ist.`;
    case 'SKILL_MOVED':
      return `Der Skill "${notificationAction.movedTitle}" wurde zum Skill "${notificationAction.parentTitle}" verschoben. Bitte prüfe, ob die Zuweisung für Dich weiterhin korrekt ist.`;
    case 'SUGGESTION_CREATED':
      return `Der Vorschlag\n${inQuotation(getTillNow(notificationAction.suggestionAction, getSkillName))} ${getType(notificationAction.suggestionAction)} ${inQuotation(getNew(notificationAction.suggestionAction, getSkillName, getCertificateName))}\nwurde erstellt.`;
    case 'SUGGESTION_ACCEPTED':
      return `Der Vorschlag\n${inQuotation(getTillNow(notificationAction.suggestionAction, getSkillName))} ${getType(notificationAction.suggestionAction)} ${inQuotation(getNew(notificationAction.suggestionAction, getSkillName, getCertificateName))}\nwurde akzeptiert.`;
    case 'SUGGESTION_REJECTED':
      return `Der Vorschlag\n${inQuotation(getTillNow(notificationAction.suggestionAction, getSkillName))} ${getType(notificationAction.suggestionAction)} ${inQuotation(getNew(notificationAction.suggestionAction, getSkillName, getCertificateName))}\nwurde abgelehnt.${getReason(notificationAction.reason)}`;
    default:
      return notificationAction satisfies never;
  }
}

function inQuotation(text: string): string {
  if (text === '') return '';
  return `"${text}"`;
}

function getReason(reason?: string): string {
  if (!reason) return '';
  return `\n\nGrund:\n"${reason}"`;
}

export function getNotificationEditButtonLabel(notification: NotificationAction): string {
  switch (notification.type) {
    case 'CHECK_VISIBILITY':
      return 'Sichtbarkeit einstellen';
    case 'SKILL_MERGED':
      return 'Gehe zu Skill';
    case 'SKILL_MOVED':
      return 'Gehe zu Skill';
    case 'SUGGESTION_CREATED':
      return 'Gehe zu Vorschlägen';
    case 'SUGGESTION_ACCEPTED':
      return '';
    case 'SUGGESTION_REJECTED':
      return '';
    default:
      return notification satisfies never;
  }
}
